import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, eur, CATEGORIES } from "@/lib/api";
import { Page } from "@/components/Page";

const inp = "h-11 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-4 text-sm outline-none focus:border-[#D4AF37]/60";
const EMPTY = { name: "", brand: "", category: "Oberteil", gender: "unisex", price: "", color: "#888888", color_name: "", image_url: "", affiliate_url: "", tags: [] };

function Stats({ s }) {
  const cards = [["Nutzer", s.users], ["Affiliate-Klicks", s.affiliate_clicks], ["Aktive Anzeigen", s.active_listings], ["Verkäufe", s.sales], ["Umsatz Marktplatz", eur(s.gmv)], ["Provision verdient", eur(s.commission_total)]];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="admin-stats">
      {cards.map(([l, v]) => <div key={l} className="surface p-5"><p className="text-[10px] uppercase tracking-widest text-neutral-500">{l}</p><p className="mt-2 font-serif text-2xl" data-testid={`admin-stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>{v}</p></div>)}
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [edit, setEdit] = useState(null);

  const load = () => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/admin/orders").then(({ data }) => setOrders(data));
  };
  useEffect(load, []);

  const save = async (e) => {
    e.preventDefault();
    const body = { ...edit, price: parseFloat(edit.price) || 0, tags: typeof edit.tags === "string" ? edit.tags.split(",").map((t) => t.trim()).filter(Boolean) : edit.tags };
    try {
      if (edit.id) await api.put(`/products/${edit.id}`, body); else await api.post("/products", body);
      toast.success("Produkt gespeichert"); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => { await api.delete(`/products/${id}`); toast.success("Produkt entfernt"); load(); };
  const payout = async (o) => { await api.patch(`/admin/orders/${o.id}`, { payout_status: o.payout_status === "offen" ? "ausgezahlt" : "offen" }); load(); };
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });

  return (
    <Page over="Admin" title="vyne Steuerzentrale" sub="Katalog & Affiliate-Links pflegen, Klicks verfolgen und Marktplatz-Auszahlungen verwalten." testid="admin-page">
      {stats && <Stats s={stats} />}
      {stats?.clicks_by_brand?.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="clicks-by-brand">{stats.clicks_by_brand.map((b) => <span key={b.brand} className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-300">{b.brand}: {b.clicks} Klicks</span>)}</div>
      )}

      <section className="mt-12">
        <div className="mb-4 flex items-center justify-between"><p className="label-over">Partner-Katalog ({products.length})</p>
          <button onClick={() => setEdit(EMPTY)} className="btn-cream !py-2" data-testid="admin-add-product-button"><Plus className="h-4 w-4" /> Produkt</button></div>
        <div className="surface overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm" data-testid="admin-products-table">
            <thead className="text-left text-[10px] uppercase tracking-widest text-neutral-500"><tr>{["", "Produkt", "Marke", "Kategorie", "Preis", "Affiliate-Link", ""].map((h, i) => <th key={i} className="p-4 font-normal">{h}</th>)}</tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-white/[0.06]" data-testid={`admin-product-row-${p.id}`}>
                  <td className="p-3"><img src={p.image_url} alt="" className="h-12 w-10 rounded object-cover" /></td>
                  <td className="p-3">{p.name}</td><td className="p-3 text-neutral-400">{p.brand}</td><td className="p-3 text-neutral-400">{p.category}</td><td className="p-3">{eur(p.price)}</td>
                  <td className="max-w-[220px] truncate p-3 text-xs text-neutral-500">{p.affiliate_url}</td>
                  <td className="p-3"><div className="flex gap-2">
                    <button onClick={() => setEdit({ ...p, tags: (p.tags || []).join(", ") })} className="text-neutral-400 hover:text-white" data-testid={`admin-edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(p.id)} className="text-neutral-400 hover:text-red-400" data-testid={`admin-delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-12">
        <p className="label-over mb-4">Marktplatz-Verkäufe & Auszahlungen</p>
        <div className="surface overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm" data-testid="admin-orders-table">
            <thead className="text-left text-[10px] uppercase tracking-widest text-neutral-500"><tr>{["Artikel", "Verkäufer:in", "PayPal", "Preis", "Provision", "Auszahlung", "Status"].map((h) => <th key={h} className="p-4 font-normal">{h}</th>)}</tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-white/[0.06]">
                  <td className="p-3">{o.title}</td><td className="p-3 text-neutral-400">{o.seller_name}</td><td className="p-3 text-xs text-neutral-500">{o.paypal_email}</td>
                  <td className="p-3">{eur(o.amount)}</td><td className="p-3 text-[#D4AF37]">{eur(o.commission)}</td><td className="p-3">{eur(o.seller_payout)}</td>
                  <td className="p-3"><button onClick={() => payout(o)} className={`rounded-full px-3 py-1 text-xs ${o.payout_status === "ausgezahlt" ? "bg-green-500/15 text-green-400" : "bg-amber-400/15 text-amber-300"}`} data-testid={`admin-payout-toggle-${o.id}`}>{o.payout_status}</button></td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-neutral-500">Noch keine Verkäufe.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-xl border-white/10 bg-[#121215]" data-testid="admin-product-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{edit?.id ? "Produkt bearbeiten" : "Neues Produkt"}</DialogTitle></DialogHeader>
          {edit && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              <input required value={edit.name} onChange={set("name")} placeholder="Name" className={`${inp} col-span-2`} data-testid="admin-product-name" />
              <input required value={edit.brand} onChange={set("brand")} placeholder="Marke" className={inp} data-testid="admin-product-brand" />
              <input required type="number" step="0.01" value={edit.price} onChange={set("price")} placeholder="Preis" className={inp} data-testid="admin-product-price" />
              <select value={edit.category} onChange={set("category")} className={inp} data-testid="admin-product-category">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <select value={edit.gender} onChange={set("gender")} className={inp} data-testid="admin-product-gender"><option value="unisex">Unisex</option><option value="damen">Damen</option><option value="herren">Herren</option></select>
              <div className="flex gap-2"><input type="color" value={edit.color} onChange={set("color")} className="h-11 w-14 rounded-xl border border-white/10 bg-transparent" /><input value={edit.color_name} onChange={set("color_name")} placeholder="Farbname" className={inp} data-testid="admin-product-colorname" /></div>
              <input value={edit.tags} onChange={set("tags")} placeholder="Tags (Komma)" className={inp} />
              <input required value={edit.image_url} onChange={set("image_url")} placeholder="Bild-URL" className={`${inp} col-span-2`} data-testid="admin-product-image" />
              <input required value={edit.affiliate_url} onChange={set("affiliate_url")} placeholder="Affiliate-Link (z.B. Awin/Partner-Link)" className={`${inp} col-span-2`} data-testid="admin-product-affiliate" />
              <button type="submit" className="btn-cream col-span-2" data-testid="admin-product-save">Speichern</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
}
